import React from "react";
import { FaEdit, FaSpinner, FaTrash } from "react-icons/fa";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsContent,
  MDBTabsPane,
  MDBInput,
  MDBBadge,
  MDBSpinner,
} from "mdb-react-ui-kit";
import {
  uploadFile,
  mfmThumbUrl,
  mfmFramedUrl,
  mfmMediumUrl,
  mfmOriginalUrl,
  mfmPosterUrl,
  mfmVariantUrl,
  computeFileSha256,
  checkDuplicate,
  setFileVisibility,
  getSsoGroups,
  listFilesForPicker,
  getFileVariants,
  getFileFraming,
  setFileFraming,
} from "../../Utils/MfmService";
import "./MfmImageUploader.css";

const VISIBILITY_LABELS = {
  private: "Privado",
  app: "App",
  public: "Público",
  groups: "Grupos",
};

const VISIBILITY_BADGE_COLOR = {
  private: "secondary",
  groups: "warning",
  app: "info",
  public: "success",
};

/**
 * MfmImageUploader — Slot de imagen con botón de edición que abre un modal
 * de selección de fichero con dos pestañas: "Subir archivo" y "Biblioteca".
 *
 * Props:
 *  - uuid            {string}   UUID del fichero MFM actual (si ya existe)
 *  - img             {string}   URL directa de imagen (fallback si no hay uuid)
 *  - appCode         {string}   Código de app ("TEAN", "CCON"…) — obligatorio
 *  - library         {string}   Slug de biblioteca (default: "_default")
 *  - size            {string}   "thumb" | "medium" | "original"
 *  - readOnly        {boolean}  Modo solo lectura
 *  - onUpload        {function} Callback tras selección: (result) => void
 *  - onDelete        {function} Callback al borrar: () => void
 *  - className       {string}
 *  - style           {object}
 *  - placeholder     {string}
 *  - defaultVisibility {string} "private" | "app" | "public"
 *  - mediaType       {string}   "image" | "video" | null (filtra la biblioteca)
 *  - mediaFrameSize  {string|object|array|number} Tamaño exacto del marco (ej: "320x180", "320px x 180px", "320,180", {width:320,height:180}, [320,180], 320)
 *  - allowPicker     {boolean}  (legacy — biblioteca siempre disponible)
 */
export class MfmImageUploader extends React.Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.cropSurfaceRef = React.createRef();
    this.cropFrameRef = React.createRef();
    this._inputId = props.inputId || `mfm-uploader-${Math.random().toString(36).slice(2)}`;
    this._libSearchTimer = null;

    this.state = {
      // ── Modal ──────────────────────────────────────────
      modalOpen: false,
      activeTab: "upload",

      // ── Pestaña subir archivo ──────────────────────────
      pendingFile: null,
      pendingPreview: null,
      isDragging: false,
      visibility: props.defaultVisibility || "private",
      groups: [],
      selectedGroupIds: [],
      loadingGroups: false,
      groupsError: false,

      // ── Proceso de subida ──────────────────────────────
      uploading: false,
      uploadError: null,

      // ── Pestaña biblioteca ─────────────────────────────
      libFiles: [],
      libLoading: false,
      libError: null,
      libSearch: "",
      libPage: 0,
      libHasMore: true,
      libSelectedUuid: null,

      // ── Encuadre de imagen (foco con tamaño fijo) ─────────
      crop: this.normalizeCrop(props.crop),
      cropDragging: false,
      cropSurfaceSize: null,
      cropFrameSize: null,
      previewNaturalSize: null,
      persistedFramedVariantKey: null,
      persistedFramedVariantUuid: null,
    };

    this._cropDrag = null;
    this._ignoreNextPickerClick = false;
    this._framingRequestId = 0;
    this._variantRequestId = 0;
    this._cropSurfaceMeasureRaf = null;
  }

  componentDidMount() {
    this.syncFramingFromServer();
    this.syncPersistedVariantFromServer();
    window.addEventListener("resize", this.scheduleCropSurfaceMeasure);
  }

  componentWillUnmount() {
    if (this._libSearchTimer) {
      clearTimeout(this._libSearchTimer);
      this._libSearchTimer = null;
    }
    this.detachCropDragListeners();
    window.removeEventListener("resize", this.scheduleCropSurfaceMeasure);
    if (this._cropSurfaceMeasureRaf) {
      window.cancelAnimationFrame(this._cropSurfaceMeasureRaf);
      this._cropSurfaceMeasureRaf = null;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const cropChanged = JSON.stringify(prevProps.crop || null) !== JSON.stringify(this.props.crop || null);
    if (cropChanged && this.props.crop) {
      this.setState({ crop: this.normalizeCrop(this.props.crop) });
    }

    // Si cambia la imagen y no estamos en modo controlado por props.crop,
    // reiniciamos el encuadre para evitar arrastrar ajustes de otra imagen.
    if (!this.props.crop && (prevProps.uuid !== this.props.uuid || prevProps.img !== this.props.img)) {
      this.setState({
        crop: this.normalizeCrop(null),
        previewNaturalSize: null,
        persistedFramedVariantKey: null,
        persistedFramedVariantUuid: null,
      });
    }

    if (prevProps.uuid !== this.props.uuid || prevProps.mediaFormat !== this.props.mediaFormat) {
      this.syncFramingFromServer();
      this.syncPersistedVariantFromServer();
    }

    if (prevProps.mediaFrameSize !== this.props.mediaFrameSize) {
      this.syncPersistedVariantFromServer();
    }

    const justOpened = !prevState.modalOpen && this.state.modalOpen;
    const previewStateChanged = prevState.pendingPreview !== this.state.pendingPreview;
    const previewChanged = prevProps.uuid !== this.props.uuid || prevProps.img !== this.props.img;
    const frameChanged = prevProps.mediaFrameSize !== this.props.mediaFrameSize;
    if (justOpened || previewChanged || frameChanged || previewStateChanged) {
      this.scheduleCropSurfaceMeasure();
    }

    if (previewStateChanged || previewChanged) {
      this.setState({ previewNaturalSize: null });
    }
  }

  scheduleCropSurfaceMeasure = () => {
    if (this._cropSurfaceMeasureRaf) {
      window.cancelAnimationFrame(this._cropSurfaceMeasureRaf);
    }

    this._cropSurfaceMeasureRaf = window.requestAnimationFrame(() => {
      this._cropSurfaceMeasureRaf = null;
      const cropSurface = this.cropSurfaceRef.current;
      if (!cropSurface) {
        if (this.state.cropSurfaceSize !== null) {
          this.setState({ cropSurfaceSize: null });
        }
        return;
      }

      const width = Math.round(cropSurface.clientWidth || 0);
      const height = Math.round(cropSurface.clientHeight || 0);
      if (width <= 0 || height <= 0) return;

      const prevSize = this.state.cropSurfaceSize;
      const cropFrame = this.cropFrameRef.current;
      const frameWidth = Math.round(cropFrame?.clientWidth || 0);
      const frameHeight = Math.round(cropFrame?.clientHeight || 0);
      const prevFrameSize = this.state.cropFrameSize;

      const surfaceUnchanged = prevSize?.width === width && prevSize?.height === height;
      const frameUnchanged = prevFrameSize?.width === frameWidth && prevFrameSize?.height === frameHeight;
      if (surfaceUnchanged && frameUnchanged) return;

      this.setState({
        cropSurfaceSize: { width, height },
        cropFrameSize: frameWidth > 0 && frameHeight > 0 ? { width: frameWidth, height: frameHeight } : null,
      });
    });
  };

  getFormatConfig = () => {
    const raw = this.props.mediaFormat;
    if (raw == null) return null;

    if (typeof raw === "number") {
      if (Number.isFinite(raw) && raw > 0) {
        return {
          formatKey: `ratio:${raw}:1`,
          aspectRatio: `${raw} / 1`,
          ratio: raw,
        };
      }
      return null;
    }

    if (typeof raw !== "string") return null;

    const preset = raw.trim().toLowerCase();
    const presets = {
      square: { aspectRatio: "1 / 1", ratio: 1 / 1 },
      circle: { aspectRatio: "1 / 1", ratio: 1 / 1, frameShape: "circle" },
      portrait: { aspectRatio: "3 / 4", ratio: 3 / 4 },
      landscape: { aspectRatio: "16 / 9", ratio: 16 / 9 },
      wide: { aspectRatio: "21 / 9", ratio: 21 / 9 },
      banner: { aspectRatio: "3 / 1", ratio: 3 / 1 },
      story: { aspectRatio: "9 / 16", ratio: 9 / 16 },
    };

    if (presets[preset]) {
      return {
        formatKey: `preset:${preset}`,
        aspectRatio: presets[preset].aspectRatio,
        ratio: presets[preset].ratio,
        frameShape: presets[preset].frameShape || null,
      };
    }

    const ratioMatch = preset.match(/^(\d+(?:\.\d+)?)\s*[:/x]\s*(\d+(?:\.\d+)?)$/);
    if (ratioMatch) {
      const w = Number(ratioMatch[1]);
      const h = Number(ratioMatch[2]);
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        return {
          formatKey: `ratio:${w}:${h}`,
          aspectRatio: `${w} / ${h}`,
          ratio: w / h,
        };
      }
    }

    return { formatKey: `format:${preset}`, aspectRatio: null, ratio: null };
  };

  getFormatRatio = () => {
    const format = this.getFormatConfig();
    if (Number.isFinite(format?.ratio) && format.ratio > 0) {
      return format.ratio;
    }

    const aspect = format?.aspectRatio;
    if (!aspect) return 16 / 9;

    const ratioMatch = String(aspect).match(/^\s*(\d+(?:\.\d+)?)\s*[/:]\s*(\d+(?:\.\d+)?)\s*$/);
    if (!ratioMatch) return 16 / 9;

    const w = Number(ratioMatch[1]);
    const h = Number(ratioMatch[2]);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      return 16 / 9;
    }
    return w / h;
  };

  getFrameSizeConfig = () => {
    const raw = this.props.mediaFrameSize
      ?? this.props.frameSize
      ?? this.props.cropFrameSize
      ?? this.props.mediaCropSize;
    if (raw == null) return null;

    const normalize = (w, h) => {
      const width = Number(w);
      const height = Number(h);
      if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return null;
      }
      return {
        width: Math.round(width),
        height: Math.round(height),
      };
    };

    if (typeof raw === "number") {
      const ratio = this.getFormatRatio();
      return normalize(raw, raw / ratio);
    }

    if (Array.isArray(raw) && raw.length >= 2) {
      return normalize(raw[0], raw[1]);
    }

    if (typeof raw === "object") {
      return normalize(
        raw.width ?? raw.w ?? raw.widthPx ?? raw.ancho,
        raw.height ?? raw.h ?? raw.heightPx ?? raw.alto,
      );
    }

    if (typeof raw === "string") {
      const cleaned = raw.trim().toLowerCase();

      // Acepta formatos: 320x180, 320 x 180, 320pxx180px, 320,180, 320/180, 320×180
      const pairMatch = cleaned.match(/^\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*[x*,:;\/\s\u00d7-]\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*$/);
      if (pairMatch) {
        return normalize(pairMatch[1], pairMatch[2]);
      }

      // Acepta formato JSON string: {"width":320,"height":180}
      if (cleaned.startsWith("{") && cleaned.endsWith("}")) {
        try {
          const parsed = JSON.parse(cleaned);
          return normalize(
            parsed.width ?? parsed.w ?? parsed.widthPx ?? parsed.ancho,
            parsed.height ?? parsed.h ?? parsed.heightPx ?? parsed.alto,
          );
        } catch {
          // Si no es JSON válido, seguimos con otros parseos.
        }
      }

      const numberMatch = cleaned.match(/^(\d+(?:\.\d+)?)$/);
      if (numberMatch) {
        const ratio = this.getFormatRatio();
        return normalize(Number(numberMatch[1]), Number(numberMatch[1]) / ratio);
      }
    }

    return null;
  };

  syncFramingFromServer = async () => {
    if (this.props.crop) return;

    const uuid = this.props.uuid;
    const format = this.getFormatConfig();
    if (!uuid || !format?.formatKey || !this.canUseCrop()) return;

    const requestId = ++this._framingRequestId;
    try {
      const framing = await getFileFraming(uuid, format.formatKey);
      if (requestId !== this._framingRequestId) return;
      if (framing && framing.x != null && framing.y != null && framing.zoom != null) {
        this.setState({
          crop: this.normalizeCrop({ x: framing.x, y: framing.y, zoom: framing.zoom }),
        });
      }
    } catch {
      // El encuadre es opcional: ignoramos error para no bloquear el flujo.
    }
  };

  buildFramedVariantKey = (formatKey, width, height, cropRaw) => {
    if (!formatKey || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return null;
    }
    const crop = this.normalizeCrop(cropRaw);
    const fixedX = Number(crop.x).toFixed(3);
    const fixedY = Number(crop.y).toFixed(3);
    const fixedZoom = Number(crop.zoom).toFixed(3);
    return `framed:${formatKey}:${Math.round(width)}x${Math.round(height)}:x${fixedX}:y${fixedY}:z${fixedZoom}`;
  };

  syncPersistedVariantFromServer = async () => {
    const uuid = this.props.uuid;
    const format = this.getFormatConfig();
    const frameSize = this.getFrameSizeConfig();

    if (!uuid || !this.canUseCrop() || !format?.formatKey || !frameSize?.width || !frameSize?.height) {
      return;
    }

    const requestId = ++this._variantRequestId;
    try {
      const [framing, variants] = await Promise.all([
        getFileFraming(uuid, format.formatKey),
        getFileVariants(uuid),
      ]);

      if (requestId !== this._variantRequestId) return;

      const crop = framing && framing.x != null && framing.y != null && framing.zoom != null
        ? this.normalizeCrop({ x: framing.x, y: framing.y, zoom: framing.zoom })
        : null;

      if (!crop || !Array.isArray(variants) || variants.length === 0) return;

      const expectedKey = this.buildFramedVariantKey(
        format.formatKey,
        frameSize.width,
        frameSize.height,
        crop,
      );

      if (!expectedKey) return;

      const exact = variants.find((v) => String(v?.variantKey || "") === expectedKey);
      const fallbackLegacy = variants.find((v) => {
        const key = String(v?.variantKey || "");
        return key.startsWith(`framed:${format.formatKey}:${Math.round(frameSize.width)}x${Math.round(frameSize.height)}:`);
      });
      const fallbackByDimensions = variants.find((v) => {
        const key = String(v?.variantKey || "");
        const w = Number(v?.width);
        const h = Number(v?.height);
        return key.startsWith("framed:")
          && Number.isFinite(w)
          && Number.isFinite(h)
          && Math.round(w) === Math.round(frameSize.width)
          && Math.round(h) === Math.round(frameSize.height);
      });
      const matched = exact || fallbackLegacy || fallbackByDimensions;

      if (matched?.variantKey) {
        this.setState({
          persistedFramedVariantUuid: uuid,
          persistedFramedVariantKey: String(matched.variantKey),
        });
      }
    } catch {
      // Si no hay permisos o no existe variante persistida todavía, mantenemos fallback framed.
    }
  };

  persistFraming = async (targetUuid) => {
    if (!this.canUseCrop()) return null;

    const format = this.getFormatConfig();
    if (!targetUuid || !format?.formatKey) return null;

    const crop = this.normalizeCrop(this.state.crop);
    const shouldPersist = this.props.persistFraming !== false;
    const frameSize = this.getFrameSizeConfig();
    const frameWidth = Number(frameSize?.width);
    const frameHeight = Number(frameSize?.height);
    const hasValidFrameSize = Number.isFinite(frameWidth)
      && Number.isFinite(frameHeight)
      && frameWidth > 0
      && frameHeight > 0;
    const primaryPayload = {
      formatKey: format.formatKey,
      x: crop.x,
      y: crop.y,
      zoom: crop.zoom,
      persist: shouldPersist,
      crop,
      ...(hasValidFrameSize
        ? { width: Math.round(frameWidth), height: Math.round(frameHeight) }
        : {}),
    };

    const primaryResult = await setFileFraming(targetUuid, primaryPayload);

    // Si el mismo origen alimenta varias vistas con ratios distintos,
    // permite persistir formatos adicionales en una sola acción de guardado.
    if (!shouldPersist) {
      return primaryResult;
    }

    const parseExtraFormat = (entry) => {
      if (!entry) return null;
      if (typeof entry === "string") {
        const raw = entry.trim();
        if (!raw) return null;
        const ratio = raw.match(/^(\d+)\s*\/\s*(\d+)$/);
        if (ratio) {
          return {
            formatKey: `ratio:${ratio[1]}:${ratio[2]}`,
            width: Number(ratio[1]),
            height: Number(ratio[2]),
          };
        }
        return { formatKey: raw };
      }
      if (typeof entry === "object") {
        if (typeof entry.formatKey === "string" && entry.formatKey.trim()) {
          const w = Number(entry.width ?? entry.w);
          const h = Number(entry.height ?? entry.h);
          return {
            formatKey: entry.formatKey.trim(),
            ...(Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0
              ? { width: Math.round(w), height: Math.round(h) }
              : {}),
          };
        }
        const w = Number(entry.width ?? entry.w);
        const h = Number(entry.height ?? entry.h);
        if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
          return {
            formatKey: `ratio:${Math.round(w)}:${Math.round(h)}`,
            width: Math.round(w),
            height: Math.round(h),
          };
        }
      }
      return null;
    };

    const extras = Array.isArray(this.props.extraPersistFormats)
      ? this.props.extraPersistFormats
      : [];
    const extraFormats = extras
      .map(parseExtraFormat)
      .filter((entry) => !!entry?.formatKey && entry.formatKey !== format.formatKey);
    const uniqueExtraFormats = Array.from(
      new Map(extraFormats.map((entry) => [entry.formatKey, entry])).values(),
    );

    for (const entry of uniqueExtraFormats) {
      const formatKey = entry.formatKey;
      try {
        await setFileFraming(targetUuid, {
          formatKey,
          x: crop.x,
          y: crop.y,
          zoom: crop.zoom,
          persist: true,
          crop,
          ...(Number.isFinite(entry.width) && Number.isFinite(entry.height)
            ? { width: entry.width, height: entry.height }
            : {}),
        });
      } catch (error) {
        // No bloquea el guardado principal; solo notifica en consola.
        console.warn("No se pudo persistir formato adicional", formatKey, error);
      }
    }

    return primaryResult;
  };

  getPersistedVariantKeyFromResult = (framingResult) => {
    const key = framingResult?.variant?.variantKey;
    return (typeof key === "string" && key.trim()) ? key.trim() : null;
  };

  normalizeCrop = (raw) => {
    const zoom = Number(raw?.zoom);
    const x = Number(raw?.x);
    const y = Number(raw?.y);

    return {
      zoom: Number.isFinite(zoom) ? Math.min(3, Math.max(1, zoom)) : 1,
      x: Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : 50,
      y: Number.isFinite(y) ? Math.min(100, Math.max(0, y)) : 50,
    };
  };

  updateCrop = (nextPatch) => {
    this.setState((prev) => {
      const crop = this.normalizeCrop({ ...prev.crop, ...nextPatch });
      this.props.onCropChange && this.props.onCropChange(crop);
      return { crop };
    });
  };

  applyCropDelta = (deltaX, deltaY) => {
    this.setState((prev) => {
      const crop = this.normalizeCrop({
        ...prev.crop,
        x: prev.crop.x - deltaX,
        y: prev.crop.y - deltaY,
      });
      this.props.onCropChange && this.props.onCropChange(crop);
      return { crop };
    });
  };

  resetCrop = () => {
    const crop = this.normalizeCrop(null);
    this.setState({ crop });
    this.props.onCropChange && this.props.onCropChange(crop);
  };

  canUseCrop = () => {
    if (this.props.enableCrop === false) return false;
    return this.props.mediaType !== "video";
  };

  getCropImageStyle = () => {
    const { crop, cropDragging, cropFrameSize, previewNaturalSize } = this.state;
    if (!this.canUseCrop()) return undefined;

    const fallbackStyle = {
      objectPosition: `${crop.x}% ${crop.y}%`,
      transform: `scale(${crop.zoom})`,
      transformOrigin: `${crop.x}% ${crop.y}%`,
    };

    const frameW = Number(cropFrameSize?.width);
    const frameH = Number(cropFrameSize?.height);
    const origW = Number(previewNaturalSize?.width);
    const origH = Number(previewNaturalSize?.height);

    if (!Number.isFinite(frameW) || !Number.isFinite(frameH)
      || !Number.isFinite(origW) || !Number.isFinite(origH)
      || frameW <= 0 || frameH <= 0 || origW <= 0 || origH <= 0) {
      return fallbackStyle;
    }

    const safeX = Math.max(0, Math.min(100, Number(crop.x)));
    const safeY = Math.max(0, Math.min(100, Number(crop.y)));
    const safeZoom = Math.max(1, Math.min(3, Number(crop.zoom) || 1));

    // Replica la misma matemática que generateFramedVariant en backend:
    // cover base + zoom + foco (x/y) en coordenadas del original + clamp a bordes.
    const baseScale = Math.max(frameW / origW, frameH / origH);
    const drawW = Math.max(1, origW * baseScale * safeZoom);
    const drawH = Math.max(1, origH * baseScale * safeZoom);

    const focusX = (safeX / 100) * drawW;
    const focusY = (safeY / 100) * drawH;

    let left = (frameW / 2) - focusX;
    let top = (frameH / 2) - focusY;

    const minLeft = Math.min(0, frameW - drawW);
    const minTop = Math.min(0, frameH - drawH);
    left = Math.max(minLeft, Math.min(0, left));
    top = Math.max(minTop, Math.min(0, top));

    return {
      position: "absolute",
      width: `${drawW}px`,
      height: `${drawH}px`,
      left: `${left}px`,
      top: `${top}px`,
      maxWidth: "none",
      maxHeight: "none",
      objectFit: "fill",
      transform: "none",
      transformOrigin: "center center",
      transition: cropDragging
        ? "none"
        : "left 0.12s ease-out, top 0.12s ease-out, width 0.12s ease-out, height 0.12s ease-out",
    };
  };

  handleCropPreviewImageLoad = (event) => {
    const naturalWidth = Number(event?.target?.naturalWidth);
    const naturalHeight = Number(event?.target?.naturalHeight);
    if (!Number.isFinite(naturalWidth) || !Number.isFinite(naturalHeight)
      || naturalWidth <= 0 || naturalHeight <= 0) {
      return;
    }

    const prev = this.state.previewNaturalSize;
    if (prev?.width === naturalWidth && prev?.height === naturalHeight) {
      this.scheduleCropSurfaceMeasure();
      return;
    }

    this.setState(
      { previewNaturalSize: { width: naturalWidth, height: naturalHeight } },
      this.scheduleCropSurfaceMeasure,
    );
  };

  getDropzoneStyle = () => {
    const frameSize = this.getFrameSizeConfig();
    if (!frameSize) {
      return {
        height: "clamp(340px, 66vh, 760px)",
        minHeight: 280,
      };
    }

    const verticalPadding = 120;
    return {
      height: `min(calc(${frameSize.height}px + ${verticalPadding}px), 72vh)`,
      minHeight: `min(calc(${frameSize.height}px + 84px), 72vh)`,
      flex: "0 0 auto",
    };
  };

  getCropFrameStyle = () => {
    const format = this.getFormatConfig();
    const isCircle = format?.frameShape === "circle";

    const frameSize = this.getFrameSizeConfig();
    if (frameSize) {
      const cropSurfaceSize = this.state.cropSurfaceSize;
      const targetWidth = frameSize.width;
      const targetHeight = frameSize.height;

      if (cropSurfaceSize?.width && cropSurfaceSize?.height) {
        const maxWidth = cropSurfaceSize.width * 0.8;
        const maxHeight = cropSurfaceSize.height * 0.8;
        const scale = Math.min(maxWidth / targetWidth, maxHeight / targetHeight, 1);
        const scaledWidth = Math.max(1, Math.round(targetWidth * scale));
        const scaledHeight = Math.max(1, Math.round(targetHeight * scale));

        return {
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          borderRadius: isCircle ? "50%" : undefined,
        };
      }

      return {
        width: `${targetWidth}px`,
        height: `${targetHeight}px`,
        maxWidth: "80%",
        maxHeight: "80%",
        borderRadius: isCircle ? "50%" : undefined,
      };
    }

    const ratio = this.getFormatRatio();
    if (ratio >= 1) {
      return {
        width: "80%",
        height: `calc(80% / ${ratio})`,
        maxHeight: "80%",
        borderRadius: isCircle ? "50%" : undefined,
      };
    }
    return {
      height: "80%",
      width: `calc(80% * ${ratio})`,
      maxWidth: "80%",
      borderRadius: isCircle ? "50%" : undefined,
    };
  };

  handleDropzoneClick = () => {
    if (this._ignoreNextPickerClick) {
      this._ignoreNextPickerClick = false;
      return;
    }
    this.fileInputRef.current && this.fileInputRef.current.click();
  };

  attachCropDragListeners = () => {
    window.addEventListener("mousemove", this.onCropMouseMove);
    window.addEventListener("mouseup", this.onCropMouseUp);
  };

  detachCropDragListeners = () => {
    window.removeEventListener("mousemove", this.onCropMouseMove);
    window.removeEventListener("mouseup", this.onCropMouseUp);
  };

  onCropMouseDown = (e) => {
    if (!this.canUseCrop()) return;
    if (e.button !== 0) return;
    // No iniciar arrastre cuando se pulsa un control interactivo.
    if (e.target && typeof e.target.closest === "function" && e.target.closest("button, a, input, select, textarea")) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    this._cropDrag = {
      x: e.clientX,
      y: e.clientY,
      moved: false,
    };
    this.setState({ cropDragging: true });
    this.attachCropDragListeners();
  };

  onCropMouseMove = (e) => {
    if (!this._cropDrag) return;
    const { x, y } = this._cropDrag;
    const dx = e.clientX - x;
    const dy = e.clientY - y;

    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
      this._cropDrag.moved = true;
      this._ignoreNextPickerClick = true;
    }

    const cropFrame = this.cropFrameRef.current;
    const cropSurface = this.cropSurfaceRef.current;
    const frameRect = cropFrame ? cropFrame.getBoundingClientRect() : null;
    const surfaceRect = cropSurface ? cropSurface.getBoundingClientRect() : null;
    const width = frameRect?.width > 0 ? frameRect.width : (surfaceRect?.width > 0 ? surfaceRect.width : 380);
    const height = frameRect?.height > 0 ? frameRect.height : (surfaceRect?.height > 0 ? surfaceRect.height : 220);

    const scaleFactorX = 100 / width;
    const scaleFactorY = 100 / height;
    this.applyCropDelta(dx * scaleFactorX, dy * scaleFactorY);

    this._cropDrag.x = e.clientX;
    this._cropDrag.y = e.clientY;
  };

  onCropMouseUp = () => {
    if (!this._cropDrag) return;
    this._cropDrag = null;
    this.setState({ cropDragging: false });
    this.detachCropDragListeners();
  };

  onCropWheel = (e) => {
    if (!this.canUseCrop()) return;
    e.preventDefault();
    e.stopPropagation();
    this._ignoreNextPickerClick = true;

    const delta = -e.deltaY * 0.003;
    this.updateCrop({ zoom: this.state.crop.zoom + delta });
  };

  // ── URL helpers ───────────────────────────────────────────────────────────

  getDisplayUrl(useFramed = true) {
    const { uuid, img, size } = this.props;
    if (uuid) {
      if (useFramed && this.canUseCrop()) {
        const hasPersistedVariant = this.state.persistedFramedVariantUuid === uuid
          && !!this.state.persistedFramedVariantKey;
        if (hasPersistedVariant) {
          return mfmVariantUrl(uuid, this.state.persistedFramedVariantKey);
        }

        const format = this.getFormatConfig();
        if (format?.formatKey) {
          const frameSize = this.getFrameSizeConfig();
          const crop = this.props.displayCropPreview === true
            ? this.normalizeCrop(this.state.crop)
            : undefined;

          if (frameSize?.width && frameSize?.height) {
            return mfmFramedUrl(uuid, frameSize.width, frameSize.height, format.formatKey, crop);
          }

          const ratio = this.getFormatRatio();
          if (Number.isFinite(ratio) && ratio > 0) {
            const base = 640;
            const width = ratio >= 1 ? base : Math.round(base * ratio);
            const height = ratio >= 1 ? Math.round(base / ratio) : base;
            return mfmFramedUrl(uuid, width, height, format.formatKey, crop);
          }
        }
      }

      if (size === "thumb") return mfmThumbUrl(uuid);
      if (size === "original") return mfmOriginalUrl(uuid);
      return mfmMediumUrl(uuid);
    }
    return img || null;
  }

  getEditorUrl = () => {
    const { uuid, img, size } = this.props;
    if (!uuid) return img || null;

    // En edición de encuadre usamos el original para evitar diferencias
    // introducidas por una variante intermedia (ej: size=medium).
    if (this.canUseCrop()) {
      return mfmOriginalUrl(uuid);
    }

    if (size === "thumb") return mfmThumbUrl(uuid);
    if (size === "original") return mfmOriginalUrl(uuid);
    return mfmMediumUrl(uuid);
  };

  // ── Modal control ─────────────────────────────────────────────────────────

  openModal = () => {
    this.setState({
      modalOpen: true,
      activeTab: "upload",
      pendingFile: null,
      pendingPreview: null,
      isDragging: false,
      uploadError: null,
      visibility: this.props.defaultVisibility || "private",
      groups: [],
      selectedGroupIds: [],
      loadingGroups: false,
      groupsError: false,
      uploading: false,
      libFiles: [],
      libLoading: false,
      libError: null,
      libSearch: "",
      libPage: 0,
      libHasMore: true,
      libSelectedUuid: null,
    });
  };

  closeModal = () => {
    this.setState({ modalOpen: false, uploading: false });
  };

  // ── Tab switching ─────────────────────────────────────────────────────────

  switchTab = (tab) => {
    this.setState({ activeTab: tab });
    if (tab === "library" && this.state.libFiles.length === 0 && !this.state.libLoading) {
      this.loadLibrary(true);
    }
  };

  // ── Upload tab — selección de fichero ─────────────────────────────────────

  handleFileChosen = async (file) => {
    if (!file) return;
    let preview = null;
    if (file.type.startsWith("image/")) {
      preview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    }
    this.setState({ pendingFile: file, pendingPreview: preview, uploadError: null });

    const { appCode } = this.props;
    if (appCode && this.state.groups.length === 0 && !this.state.loadingGroups) {
      this.loadGroups();
    }
  };

  loadGroups = async () => {
    this.setState({ loadingGroups: true, groupsError: false });
    try {
      const groups = await getSsoGroups(this.props.appCode);
      this.setState({ groups: groups || [], loadingGroups: false });
    } catch {
      this.setState({ loadingGroups: false, groupsError: true });
    }
  };

  handleInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (file) this.handleFileChosen(file);
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) this.handleFileChosen(file);
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    this.setState({ isDragging: false });
  };

  // ── Upload tab — confirmar subida ─────────────────────────────────────────

  handleConfirmUpload = async () => {
    const { pendingFile, visibility, selectedGroupIds } = this.state;
    const { appCode, library } = this.props;
    if (!pendingFile && !this.props.uuid) return;

    this.setState({ uploading: true, uploadError: null });
    try {
      if (!pendingFile && this.props.uuid) {
        const framingResult = await this.persistFraming(this.props.uuid);
        const persistedVariantKey = this.getPersistedVariantKeyFromResult(framingResult);
        if (persistedVariantKey) {
          this.setState({
            persistedFramedVariantUuid: this.props.uuid,
            persistedFramedVariantKey: persistedVariantKey,
          });
        }
        this.props.onUpload && this.props.onUpload({
          uuid: this.props.uuid,
          crop: this.state.crop,
          framingOnly: true,
          framedVariantKey: persistedVariantKey,
        });
        this.closeModal();
        return;
      }

      const contentHash = await computeFileSha256(pendingFile);
      const groupIds = visibility === "groups" ? selectedGroupIds : [];

      // Deduplicación por SHA-256
      const duplicate = await checkDuplicate(contentHash, appCode, visibility, groupIds, library);
      if (duplicate) {
        const framingResult = await this.persistFraming(duplicate.uuid);
        const persistedVariantKey = this.getPersistedVariantKeyFromResult(framingResult);
        if (persistedVariantKey) {
          this.setState({
            persistedFramedVariantUuid: duplicate.uuid,
            persistedFramedVariantKey: persistedVariantKey,
          });
        }
        this.props.onUpload && this.props.onUpload({
          ...duplicate,
          crop: this.state.crop,
          framedVariantKey: persistedVariantKey,
        });
        this.closeModal();
        return;
      }

      const result = await uploadFile(pendingFile, { appCode, library, visibility, contentHash });

      if (visibility === "groups" && groupIds.length > 0) {
        await setFileVisibility(result.uuid, "groups", groupIds);
      }

      const framingResult = await this.persistFraming(result.uuid);
      const persistedVariantKey = this.getPersistedVariantKeyFromResult(framingResult);
      if (persistedVariantKey) {
        this.setState({
          persistedFramedVariantUuid: result.uuid,
          persistedFramedVariantKey: persistedVariantKey,
        });
      }

      this.props.onUpload && this.props.onUpload({
        ...result,
        crop: this.state.crop,
        framedVariantKey: persistedVariantKey,
      });
      this.closeModal();
    } catch (err) {
      this.setState({ uploading: false, uploadError: err.message || "Error al subir el fichero" });
    }
  };

  // ── Library tab ───────────────────────────────────────────────────────────

  loadLibrary = async (reset = true) => {
    const { appCode, mediaType } = this.props;
    const { libSearch, libPage } = this.state;
    const page = reset ? 0 : libPage;
    this.setState({ libLoading: true, libError: null });
    console.debug(`[MfmImageUploader] loadLibrary — app:${appCode} mediaType:${mediaType} search:"${libSearch}" page:${page}`);
    try {
      const files = await listFilesForPicker({
        app: appCode,
        mediaType: mediaType || undefined,
        search: libSearch || undefined,
        page,
        size: 24,
      });
      console.debug(`[MfmImageUploader] loadLibrary — respuesta: ${files.length} fichero(s)`, files);
      this.setState((prev) => ({
        libFiles: reset ? files : [...prev.libFiles, ...files],
        libPage: page + 1,
        libHasMore: files.length === 24,
        libLoading: false,
      }));
    } catch (err) {
      console.error("[MfmImageUploader] loadLibrary — error:", err);
      this.setState({ libLoading: false, libError: "Error al cargar la biblioteca" });
    }
  };

  handleLibSearch = (e) => {
    const libSearch = e.target.value;
    this.setState({ libSearch, libFiles: [], libPage: 0, libHasMore: true }, () => {
      clearTimeout(this._libSearchTimer);
      this._libSearchTimer = setTimeout(() => this.loadLibrary(true), 400);
    });
  };

  handleLibSelect = (file) => {
    this.setState({ libSelectedUuid: file.uuid });
  };

  handleConfirmLibrary = () => {
    const { libFiles, libSelectedUuid } = this.state;
    const file = libFiles.find((f) => f.uuid === libSelectedUuid);
    if (file) {
      this.props.onUpload && this.props.onUpload({
        uuid: file.uuid,
        url: file.url,
        thumbUrl: file.thumbUrl,
        mediumUrl: file.mediumUrl,
        posterUrl: file.posterUrl,
        mediaType: file.mediaType,
        originalName: file.originalName,
        deduplicated: false,
        crop: this.state.crop,
      });
    }
    this.closeModal();
  };

  buildThumb = (file) => {
    // El backend devuelve URLs relativas que no funcionan cruzando puertos en dev.
    // Siempre construimos la URL absoluta con mfmThumbUrl/mfmPosterUrl.
    if (file.mediaType === "image") return mfmThumbUrl(file.uuid);
    if (file.mediaType === "video") return mfmPosterUrl(file.uuid);
    return null;
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  handleDelete = () => {
    if (this.props.readOnly) return;
    this.props.onDelete && this.props.onDelete();
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  renderVisibilityBadge(visibility) {
    const color = VISIBILITY_BADGE_COLOR[visibility] || "secondary";
    const label = VISIBILITY_LABELS[visibility] || visibility;
    return (
      <MDBBadge color={color} pill className="ms-1" style={{ fontSize: "0.6rem" }}>
        {label}
      </MDBBadge>
    );
  }

  renderUploadTab(displayUrl) {
    const {
      pendingFile, pendingPreview, isDragging, cropDragging, crop,
      visibility, groups, selectedGroupIds, loadingGroups, groupsError,
      uploadError,
    } = this.state;

    const previewUrl = pendingPreview || displayUrl;
    const format = this.getFormatConfig();
    const frameSize = this.getFrameSizeConfig();
    const hasPreview = !!previewUrl || !!pendingFile;
    const cropImageStyle = this.getCropImageStyle();
    const cropFrameStyle = this.getCropFrameStyle();
    const dropzoneStyle = this.getDropzoneStyle();
    const canCrop = this.canUseCrop() && !!previewUrl;

    return (
      <div className="mfm-upload-tab">

        {format?.formatKey && (
          <div className="small text-muted mb-1">Formato de encuadre: {this.props.mediaFormat}</div>
        )}

        {frameSize && (
          <div className="small text-muted mb-2">
            Marco objetivo: {frameSize.width} x {frameSize.height}px
          </div>
        )}

        {/* Zona drag & drop */}
        <div
          className={`mfm-dropzone${isDragging ? " mfm-dropzone--over" : ""}${hasPreview ? " mfm-dropzone--has-file" : ""}`}
          style={dropzoneStyle}
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}
          onClick={this.handleDropzoneClick}
        >
          {!hasPreview && (
            <div className="mfm-dropzone__empty">
              <svg className="mfm-dropzone__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <p className="mfm-dropzone__text">
                Arrastra un fichero aquí o <span className="mfm-dropzone__link">haz clic para elegir</span>
              </p>
              <p className="mfm-dropzone__hint">Imágenes y vídeos</p>
            </div>
          )}

          {hasPreview && (
            <div className="mfm-dropzone__preview">
              {previewUrl
                ? (
                  <div
                    className="mfm-dropzone__preview-crop-surface"
                    ref={this.cropSurfaceRef}
                  >
                    {canCrop && (
                      <div
                        className="mfm-crop-frame mfm-crop-frame--interactive"
                        ref={this.cropFrameRef}
                        style={cropFrameStyle}
                        onMouseDown={this.onCropMouseDown}
                        onWheel={this.onCropWheel}
                        onWheelCapture={this.onCropWheel}
                      >
                        <div className="mfm-crop-frame__media">
                          <img
                            src={previewUrl}
                            alt="Vista previa"
                            className={`mfm-dropzone__preview-img mfm-dropzone__preview-img--crop${cropDragging ? " is-dragging" : ""}`}
                            style={cropImageStyle}
                            onLoad={this.handleCropPreviewImageLoad}
                            draggable={false}
                          />
                        </div>
                        <div className="mfm-crop-frame__grid" />
                      </div>
                    )}

                    {!canCrop && (
                      <img
                        src={previewUrl}
                        alt="Vista previa"
                        className="mfm-dropzone__preview-img"
                        draggable={false}
                      />
                    )}
                  </div>
                )
                : (
                  <div className="mfm-dropzone__preview-file">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                    <span className="mfm-dropzone__filename">{pendingFile.name}</span>
                  </div>
                )
              }

              {canCrop && (
                <div className="mfm-crop-inline-hint">
                  <div className="mfm-crop-inline-texts">
                    <span>Arrastra para encuadrar y usa la rueda para zoom</span>
                    <span className="mfm-crop-zoom-hint">El zoom mínimo (1×) garantiza que la imagen cubre el recuadro · Usa «Restaurar» para centrar</span>
                  </div>
                  <div className="mfm-crop-inline-controls">
                    <MDBBtn
                      size="sm"
                      color="light"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.updateCrop({ zoom: crop.zoom - 0.1 });
                      }}
                    >
                      -
                    </MDBBtn>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.01"
                      value={crop.zoom}
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.updateCrop({ zoom: Number(e.target.value) });
                      }}
                    />
                    <MDBBtn
                      size="sm"
                      color="light"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.updateCrop({ zoom: crop.zoom + 0.1 });
                      }}
                    >
                      +
                    </MDBBtn>
                  </div>
                  <MDBBtn
                    size="sm"
                    color="light"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.resetCrop();
                    }}
                  >
                    Restaurar
                  </MDBBtn>
                </div>
              )}

              <div className="mfm-dropzone__change-overlay">
                Arrastra otro fichero o haz clic para cambiar
              </div>
            </div>
          )}
        </div>

        <input
          ref={this.fileInputRef}
          type="file"
          accept={
            this.props.mediaType === "image" ? "image/*" :
            this.props.mediaType === "video" ? "video/*" :
            "image/*,video/*"
          }
          onChange={this.handleInputChange}
          style={{ display: "none" }}
        />

        {/* Selector de visibilidad — solo cuando hay fichero seleccionado */}
        {pendingFile && (
          <div className="mfm-visibility-selector mt-3">
            <p className="mfm-visibility-selector__title">Visibilidad al subir</p>
            <div className="mfm-visibility-selector__options">
              {Object.entries(VISIBILITY_LABELS).map(([v, label]) => (
                <label key={v} className="mfm-visibility-selector__option">
                  <input
                    type="radio"
                    name={`vis-${this._inputId}`}
                    value={v}
                    checked={visibility === v}
                    onChange={() => {
                      this.setState({ visibility: v });
                      if (v === "groups" && groups.length === 0 && !loadingGroups) {
                        this.loadGroups();
                      }
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>

            {visibility === "groups" && (
              <div className="mfm-visibility-selector__groups mt-2">
                {loadingGroups && <small className="text-muted">Cargando grupos…</small>}
                {!loadingGroups && groupsError && (
                  <small className="text-danger">Error al cargar grupos</small>
                )}
                {!loadingGroups && !groupsError && groups.length === 0 && (
                  <small className="text-muted">
                    No hay grupos. Créalos en el Gestor de Ficheros &rsaquo; Grupos.
                  </small>
                )}
                {!loadingGroups && !groupsError && groups.length > 0 && (
                  <select
                    className="form-select form-select-sm"
                    multiple
                    size={Math.min(groups.length, 4)}
                    value={selectedGroupIds.map(String)}
                    onChange={(e) => {
                      const ids = Array.from(e.target.selectedOptions, (o) => Number(o.value));
                      this.setState({ selectedGroupIds: ids });
                    }}
                  >
                    {groups.map((g) => (
                      <option key={g.idGroup} value={g.idGroup}>{g.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        )}

        {uploadError && (
          <div className="alert alert-danger mt-3 py-2 small">{uploadError}</div>
        )}
      </div>
    );
  }

  renderLibraryTab() {
    const { libFiles, libLoading, libError, libSearch, libHasMore, libSelectedUuid } = this.state;

    return (
      <div className="mfm-library-tab">
        <div className="mb-3">
          <MDBInput
            label="Buscar por nombre o título…"
            value={libSearch}
            onChange={this.handleLibSearch}
            size="sm"
          />
        </div>

        {libError && <div className="alert alert-danger">{libError}</div>}

        {libFiles.length === 0 && !libLoading && (
          <p className="text-muted text-center py-5">No hay ficheros disponibles</p>
        )}

        <div className="mfm-library-grid">
          {libFiles.map((file) => {
            const thumb = this.buildThumb(file);
            const isSelected = file.uuid === libSelectedUuid;
            return (
              <div
                key={file.uuid}
                className={`mfm-library-item${isSelected ? " mfm-library-item--selected" : ""}`}
                onClick={() => this.handleLibSelect(file)}
              >
                <div className="mfm-library-item__thumb">
                  {thumb
                    ? <img src={thumb} alt={file.title || file.originalName} />
                    : <div className="mfm-library-item__no-thumb">?</div>
                  }
                  {file.mediaType === "video" && (
                    <div className="mfm-library-item__play">▶</div>
                  )}
                </div>
                <div className="mfm-library-item__info">
                  <span className="mfm-library-item__name">{file.title || file.originalName}</span>
                  {file.ownerName && (
                    <span className="mfm-library-item__owner">{file.ownerName}</span>
                  )}
                  {this.renderVisibilityBadge(file.visibility)}
                </div>
              </div>
            );
          })}
        </div>

        {libLoading && (
          <div className="text-center py-3">
            <MDBSpinner size="sm" />
          </div>
        )}

        {!libLoading && libHasMore && libFiles.length > 0 && (
          <div className="text-center mt-3">
            <MDBBtn size="sm" color="light" onClick={() => this.loadLibrary(false)}>
              Cargar más
            </MDBBtn>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { className, style, readOnly, placeholder, uuid, onDelete, headless } = this.props;
    const { modalOpen, activeTab, pendingFile, libSelectedUuid, uploading, visibility, selectedGroupIds } = this.state;
    const format = this.getFormatConfig();

    const displayUrl = this.getDisplayUrl(true);
    const editorDisplayUrl = this.getEditorUrl();
    const hasFramedDisplay = !!uuid && this.canUseCrop() && !!format?.formatKey;

    const canSave = activeTab === "upload"
      ? pendingFile !== null || (!!uuid && !!format?.formatKey && this.canUseCrop())
      : libSelectedUuid !== null;

    const saveBlocked = uploading
      || !canSave
      || (activeTab === "upload" && visibility === "groups" && selectedGroupIds.length === 0);

    const imageStyle = hasFramedDisplay ? undefined : this.getCropImageStyle();

    return (
      <>
        {/* ── Slot de imagen ── */}
        {!headless && (
        <div
          className={`mfm-image-uploader${readOnly ? " readonly" : ""}${className ? " " + className : ""}`}
          style={(style && (style.aspectRatio || style.height || style.maxHeight)) || !format?.aspectRatio
            ? style
            : { ...style, aspectRatio: format.aspectRatio }}
        >
          <div className="mfm-image-container">
            {displayUrl
              ? <img src={displayUrl} alt="Media" style={imageStyle} />
              : <p className="mfm-placeholder">{placeholder || "Seleccionar imagen"}</p>
            }

            {!readOnly && (
              <div className="mfm-edit-overlay">
                <button
                  type="button"
                  className="mfm-edit-btn"
                  title="Editar fichero"
                  onClick={this.openModal}
                >
                  <FaEdit />
                </button>
                {uuid && onDelete && (
                  <button
                    type="button"
                    className="mfm-delete-btn"
                    onClick={this.handleDelete}
                    title="Eliminar"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* ── Modal ── */}
        <MDBModal
          show={modalOpen}
          onHide={this.closeModal}
          tabIndex="-1"
          className="mfm-upload-modal"
        >
          <MDBModalDialog size="xl" className="mfm-upload-modal__dialog">
            <MDBModalContent>

              <MDBModalHeader>
                <MDBModalTitle>Seleccionar fichero</MDBModalTitle>
                <MDBBtn className="btn-close" color="none" onClick={this.closeModal} />
              </MDBModalHeader>

              {/* Pestañas de navegación */}
              <div className="mfm-upload-modal__nav px-3 pt-3">
                <MDBTabs>
                  <MDBTabsItem>
                    <MDBTabsLink
                      onClick={() => this.switchTab("upload")}
                      active={activeTab === "upload"}
                    >
                      Subir archivo
                    </MDBTabsLink>
                  </MDBTabsItem>
                  <MDBTabsItem>
                    <MDBTabsLink
                      onClick={() => this.switchTab("library")}
                      active={activeTab === "library"}
                    >
                      Biblioteca
                    </MDBTabsLink>
                  </MDBTabsItem>
                </MDBTabs>
              </div>

              <MDBModalBody className="mfm-upload-modal__body">
                <MDBTabsContent className="h-100">
                  <MDBTabsPane show={activeTab === "upload"} className="h-100">
                    {this.renderUploadTab(editorDisplayUrl)}
                  </MDBTabsPane>
                  <MDBTabsPane show={activeTab === "library"} className="h-100">
                    {this.renderLibraryTab()}
                  </MDBTabsPane>
                </MDBTabsContent>
              </MDBModalBody>

              <MDBModalFooter>
                <MDBBtn color="secondary" onClick={this.closeModal} disabled={uploading}>
                  Cancelar
                </MDBBtn>
                <MDBBtn
                  color="primary"
                  onClick={activeTab === "upload" ? this.handleConfirmUpload : this.handleConfirmLibrary}
                  disabled={saveBlocked}
                >
                  {uploading
                    ? <><FaSpinner className="fa-spin me-2" />Subiendo…</>
                    : "Guardar"
                  }
                </MDBBtn>
              </MDBModalFooter>

            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      </>
    );
  }
}
